import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useApi } from './useApi';

export type MonitorStatus = 'up' | 'warn' | 'down' | 'unknown';
export type SslStatus = 'ok' | 'expiring' | 'expired' | 'invalid' | 'none' | null;

export interface Monitor {
  id: string;
  name: string;
  kind: 'server' | 'project';
  url: string | null;
  host: string | null;
  port: number | null;
  client_id: string | null;
  client_name?: string | null;
  client_logo?: string | null;
  check_ssl: boolean;
  check_meta_pixel: boolean;
  check_google: boolean;
  enabled: boolean;
  status: MonitorStatus;
  last_checked_at: string | null;
  latency_ms: number | null;
  http_code: number | null;
  error: string | null;
  ssl_status: SslStatus;
  ssl_expires_at: string | null;
  ssl_days_left: number | null;
  has_meta_pixel: boolean | null;
  has_google: boolean | null;
  uptime_24h: number | null;
}

export function useMonitors() {
  const api = useApi();
  const [monitors, setMonitors] = useState<Monitor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setMonitors(await api('/monitors'));
      setError(null);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  const createMonitor = useCallback(
    async (fields: Partial<Monitor>) => {
      try {
        const m: Monitor = await api('/monitors', { method: 'POST', body: JSON.stringify(fields) });
        setMonitors((prev) => [...prev, m]);
        toast.success('Monitor aggiunto');
      } catch (e: any) {
        toast.error(e.message || 'Creazione fallita');
        throw e;
      }
    },
    [api]
  );

  const updateMonitor = useCallback(
    async (id: string, fields: Partial<Monitor>) => {
      try {
        const m: Monitor = await api(`/monitors/${id}`, { method: 'PATCH', body: JSON.stringify(fields) });
        setMonitors((prev) => prev.map((x) => (x.id === id ? m : x)));
      } catch (e: any) {
        toast.error(e.message || 'Aggiornamento fallito');
        await load();
      }
    },
    [api, load]
  );

  const deleteMonitor = useCallback(
    async (id: string) => {
      const snap = monitors;
      setMonitors((prev) => prev.filter((x) => x.id !== id));
      try {
        await api(`/monitors/${id}`, { method: 'DELETE' });
      } catch (e: any) {
        toast.error(e.message || 'Eliminazione fallita');
        setMonitors(snap);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [api, monitors]
  );

  const checkOne = useCallback(
    async (id: string) => {
      try {
        const m: Monitor = await api(`/monitors/${id}/check`, { method: 'POST' });
        setMonitors((prev) => prev.map((x) => (x.id === id ? m : x)));
      } catch (e: any) {
        toast.error(e.message || 'Check fallito');
      }
    },
    [api]
  );

  const checkAll = useCallback(async () => {
    setChecking(true);
    try {
      setMonitors(await api('/monitors/check-all', { method: 'POST' }));
      toast.success('Controllo completato');
    } catch (e: any) {
      toast.error(e.message || 'Controllo fallito');
    } finally {
      setChecking(false);
    }
  }, [api]);

  return { monitors, loading, error, checking, reload: load, createMonitor, updateMonitor, deleteMonitor, checkOne, checkAll };
}
